import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import api from "api";
import { useAuth } from "providers/auth";
import { RenterBenefit } from "providers/auth/types";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export const useRentReportingIssuesVerifyRent = () => {
  const { partner } = useAuth();
  const navigate = useNavigate();
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    string[]
  >([]);
  const [submittedMonths, setSubmittedMonths] = useState<string[]>([]);
  const [activeRVIdx, setActiveRVIdx] = useState<number | null>(null);
  const { data: enrollment } = useQuery({
    queryKey: ["ENROLLMENT_WITH_PAYMENT_VERIFICATION"],
    queryFn: () =>
      api.getDashboard().then((res) => {
        // find enrollment with payment-verification issue or m2m-payment-verification
        return (
          res.data.enrollments.find((e) =>
            e.issues?.some(
              (i) =>
                i.slug === "payment-verification" ||
                i.slug === "m2m-payment-verification"
            )
          ) || res.data.enrollments[0]
        );
      }),
    enabled: Boolean(
      partner?.renter_benefits?.includes(RenterBenefit.RENT_REPORTING)
    )
  });

  const enrollmentId = enrollment?.issues?.find(
    (i) =>
      i.slug === "payment-verification" || i.slug === "m2m-payment-verification"
  )?.params.enrollment_id;

  useEffect(() => {
    if (enrollment?.issues && !enrollmentId) navigate("/hub");
  }, [enrollmentId, enrollment]);

  const { data: verifications, ...verificationsMeta } = useQuery({
    queryKey: ["REPORTING_HISTORY", enrollmentId],
    queryFn: () =>
      api
        .getUnverifiedRentalVerifications(enrollmentId || 0)
        .then((res) =>
          res.data.rental_verifications.filter((v) => !v.is_verified)
        )
        .then((verificationsRet) => {
          setActiveRVIdx(0);
          return verificationsRet;
        }),
    enabled: Boolean(enrollmentId)
  });

  const currentRv = useMemo(() => {
    if (verifications && activeRVIdx !== null)
      return verifications[activeRVIdx];
  }, [activeRVIdx, verifications]);

  const bankTransactionsMeta = useQuery({
    queryKey: ["BANK_TRANSACTIONS", currentRv?.id],
    queryFn: () => api.bankTransactions(enrollmentId || 0, currentRv?.id || 0),
    placeholderData: keepPreviousData,
    enabled: Boolean(currentRv?.id && enrollmentId)
  });

  const suggestBankTransactions = useMutation({
    mutationKey: ["SUGGEST_BANK_TRANSACTIONS", currentRv?.id],
    mutationFn: (data: {
      transaction_ids?: string[];
      document?: {
        url: string;
        filename: string;
        description?: string;
      };
    }) =>
      api.suggestBankTransaction(enrollmentId || 0, currentRv?.id || 0, data)
  });

  return {
    currentRv,
    setActiveRVIdx,
    verifications,
    verificationsMeta,
    bankTransactionsMeta,
    selectedTransactionIds,
    setSelectedTransactionIds,
    suggestBankTransactions,
    activeRVIdx,
    submittedMonths,
    setSubmittedMonths
  };
};
